import React, { FC, useState, useEffect } from "react";
import { Box, Grid, VStack } from "@chakra-ui/react";
import { BlockContainer } from "src/atoms";
import Renderer from "src/lib/renderer";
import { Element } from "@prismicio/react";
import paragraph from "src/lib/renderer/elements/paragraph";
import { list, oList, listItem } from "src/lib/renderer/elements/list";
import HeaderMedia from "src/slices/Header/components/HeaderMedia";
import useVisitorContext from "src/providers/VisitorContext/useVisitorContext";
import { isPersistentParam } from "src/lib/queryParams";

// Max width for the right side column from the existing Header setup
const MAX_NON_CENTERED_IMAGE_WIDTH = 584;

/**
 * Allowlist of approved origins for the registration iframe.
 * Only URLs from these origins will be rendered.
 */
const ALLOWED_ORIGINS = [
  "https://app.shine.fr",
  "https://app.staging.shine.fr",
];

/**
 * Environment-driven default URL.
 * Falls back to staging only in non-production environments.
 */
const getDefaultIframeUrl = (): string => {
  const env = process.env.GATSBY_DEPLOY_ENV;
  if (env === "production") {
    return "https://app.shine.fr/register/home";
  }
  return "https://app.staging.shine.fr/register/home";
};

/**
 * Validates and normalizes the iframe URL against the allowlist.
 * Returns null if the URL is missing, invalid, or not from an approved origin.
 */
const getSafeIframeUrl = (rawUrl: string | null | undefined): string | null => {
  const urlToParse = rawUrl || getDefaultIframeUrl();

  try {
    const parsed = new URL(urlToParse);
    const isAllowed = ALLOWED_ORIGINS.some(
      (origin) => parsed.origin === origin
    );
    if (!isAllowed) {
      console.warn(
        `[HeroOnboarding] Blocked iframe URL from non-allowlisted origin: ${parsed.origin}`
      );
      return null;
    }
    // Return normalized href (strips unexpected fragments etc.)
    return parsed.href;
  } catch {
    console.warn(`[HeroOnboarding] Invalid iframe URL: ${urlToParse}`);
    return null;
  }
};

const HeroOnboarding: FC<any> = ({ primary }) => {
  const visitorContext = useVisitorContext();
  
  // Track whether the iframe has finished loading
  const [isLoaded, setIsLoaded] = useState(false);

  // Use state to avoid hydration mismatch if tracking params alter the URL
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!primary) return;

    // Get the base URL (CMS configured or default)
    const safeBaseUrl = getSafeIframeUrl(primary.iframe_url);
    
    if (!safeBaseUrl) {
      setIframeUrl(null);
      return;
    }

    try {
      const url = new URL(safeBaseUrl);

      // Inject affiliate param
      if (visitorContext.affiliateLandingHref) {
        url.searchParams.set("affiliate", visitorContext.affiliateLandingHref);
      }

      // Inject persistent UTM params
      if (visitorContext.search) {
        const params = new URLSearchParams(visitorContext.search);
        for (const [key, val] of params.entries()) {
          if (!url.searchParams.has(key) && isPersistentParam(key)) {
            url.searchParams.set(key, val);
          }
        }
      }

      // Inject "from" param based on current location
      if (typeof window !== "undefined" && window.location.pathname) {
        url.searchParams.set("from", window.location.pathname);
      }

      setIframeUrl(url.toString());
    } catch {
      setIframeUrl(safeBaseUrl);
    }
  }, [primary, visitorContext]);

  if (!primary) return null;

  const { content, media } = primary;

  const handleIframeLoad = () => {
    // Delay reveal to give the React SPA inside the iframe time to
    // hydrate and paint, preventing a white flash after the skeleton fades.
    setTimeout(() => setIsLoaded(true), 700);
  };

  return (
    <BlockContainer
      textAlign={{ base: "left", md: "left" }}
      paddingTop={{ base: "space-16" }}
    >
      <Grid
        gridTemplateColumns={{
          base: "1fr",
          md: "1fr 1fr",
          xl: `1fr ${MAX_NON_CENTERED_IMAGE_WIDTH}px`,
        }}
        gap={{
          base: "space-16",
          md: "space-40",
          "2xl": "space-80",
        }}
        alignItems="stretch"
        bg={{ md: "yellow-0" }}
        borderRadius="intermediate"
        paddingLeft={{ md: "space-40", lg: "space-80" }}
        paddingRight={{
          md: "space-40",
          lg: "space-80",
        }}
        paddingY={{ md: 0 }}
        position="relative"
        marginBottom={{ md: "space-40" }}
        w="full"
      >
        {/* Left Side: Title, Description, and Hero Image */}
        <VStack
          alignItems="start"
          borderRadius="intermediate"
          justifyContent="center"
          textAlign="start"
          align="start"
          maxW="730px"
          spacing={{ base: "space-16", md: "space-40" }}
          bg={{ base: "yellow-0", md: "transparent" }}
          paddingX={{ base: "space-24", md: 0 }}
          paddingY={{ base: "space-24", lg: "58px" }}
          minW="full"
        >
          <VStack
            spacing={{ base: "space-16", md: "space-24" }}
            align="flex-start"
            sx={{
              "h1, h2, h3": {
                marginBottom: 0,
                color: "text-heading",
              },
            }}
            wordBreak="break-word"
          >
            <Renderer
              field={content}
              overrides={{
                [Element.paragraph]: (args) =>
                  paragraph({
                    ...args,
                    overrideProps: {
                      variant: "body-md-reg",
                    },
                  }),
                [Element.list]: (args) =>
                  list({
                    ...args,
                    overrideProps: {
                      pl: "space-16",
                    },
                  }),
                [Element.oList]: (args) =>
                  oList({
                    ...args,
                    overrideProps: {
                      pl: "space-16",
                    },
                  }),
                [Element.listItem]: (args) =>
                  listItem({
                    ...args,
                    overrideProps: {
                      variant: "body-md-reg",
                    },
                  }),
                [Element.oListItem]: (args) =>
                  listItem({
                    ...args,
                    overrideProps: {
                      variant: "body-md-reg",
                    },
                  }),
              }}
            />
          </VStack>

          {/* Left Side: Hero Image (if provided in Prismic) */}
          {media?.document ? (
            <Box w="full" mt="space-24">
              <HeaderMedia
                is_centered={false}
                hide_media_on_mobile={false}
                media={media}
              />
            </Box>
          ) : null}
        </VStack>

        {/* Right Side: Embedded Registration Iframe Card */}
        <Box
          w="full"
          mx="auto"
          alignSelf="center"
          position="relative"
          h={{ base: "600px", md: "720px" }}
          overflow="hidden"
          borderRadius="intermediate"
          bg="white"
          boxShadow="xl"
        >
          {/* Skeleton placeholder shown while URL is resolving or SPA is booting up */}
          {!isLoaded && (
            <Box position="absolute" inset={0} bg="gray.50" zIndex={1} />
          )}

          {iframeUrl && (
            <iframe
              src={iframeUrl}
              title="Shine Registration Flow"
              sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
              onLoad={handleIframeLoad}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                position: "absolute",
                top: 0,
                left: 0,
                opacity: isLoaded ? 1 : 0,
                transition: "opacity 0.3s ease",
              }}
            />
          )}
        </Box>
      </Grid>
    </BlockContainer>
  );
};

export default HeroOnboarding;
